import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {map, tap} from "rxjs/operators";
import {AbstractService} from "./abstract.service";
import {NoticeRepo} from "../repositories/api/notice.repo";
import {NoticeDto, NoticeRemindDto} from "../repositories/dto/notice.dto";
import {isDefined, notDefined} from "../libs/utils/epic.utils";

/**
 * Die offene Pflichtmitteilung und der Takt, in dem sie wiederkommt.
 *
 * ## 🔑 Das Nachfassen — Punkt 13 des Zustimmungsplans, entschieden am 2026-09-02
 *
 * **(a)** erste Anzeige beim Start · **(b)** danach erst wieder, wenn seit dem Wegklicken **60 Minuten vergangen
 * sind, in denen der Tab im Vordergrund war** · **(c)** der Zähler läuft **je Sitzung**, ein Neuladen setzt ihn nicht
 * zurück · **(d)** nur bei Zeichnungsberechtigten.
 *
 * ⚠️ **Warum Vordergrundzeit und nicht die Uhrzeit:** Sonst öffnet jemand morgens zehn Tabs und wird mittags zehnmal
 * begrüßt. Gezählt wird über `visibilitychange`, nicht über die Wanduhr.
 *
 * ⚠️ **Warum `sessionStorage` und nicht `localStorage`:** Der Zähler soll ein Neuladen überleben — sonst wird F5 zur
 * Umgehung und das Nachfassen wirkungslos —, aber nicht den Tag. `localStorage` machte aus dem Wegklicken eine
 * dauerhafte Abschaltung.
 *
 * ⛔ **Die Grenze ist nicht technisch, sondern die aus Abschnitt 2.1:** Druck darf nur dort entstehen, wo jemand
 * handeln kann. Häufiger als stündlich wäre Drängeln, und Drängeln erzeugt Widerspruch statt Unterschriften.
 *
 * ℹ️ **Ein Zähler genügt**, weil je Organisation immer nur *eine* Mitteilung offen ist (Art. 7 Abs. 2 — eine Erklärung
 * muss klar unterscheidbar sein). Der Schlüssel trägt trotzdem die Kennung: Kommt nach dem Beantworten die nächste,
 * beginnt ihre Frist bei null statt die der vorigen zu erben.
 *
 * ⚠️ **Wer zwischen zwei Organisationen wechselt, wird häufiger gefragt.** Der Zähler ist einer, die Mitteilungen sind
 * zwei — nach dem Wechsel gehört er der anderen, und die erste erscheint sofort wieder. Bewusst hingenommen: Ein
 * Zähler je Organisation wäre mehr Zustand im Browser für einen Fall, den kaum jemand hat, und die Anzeige ist ein
 * Hinweis, keine Sperre.
 */
@Injectable({providedIn: 'root'})
export class NoticeService extends AbstractService {

  /** 60 Minuten Vordergrundzeit. Bewusst eine Konstante — eine einstellbare Taktung wäre eine Einladung, sie zu verschärfen. */
  private static readonly REMIND_AFTER_MS: number = 60 * 60 * 1000;

  private static readonly STORAGE_KEY: string = 'notice:dismissed';

  private static readonly TICK_MS: number = 30_000;

  private readonly noticeRepo: NoticeRepo = this.repoProvider.getRepo(NoticeRepo);

  private readonly notice: BehaviorSubject<NoticeDto | undefined> = new BehaviorSubject<NoticeDto | undefined>(undefined);

  /** Zeitpunkt des letzten Abrechnens, solange der Tab im Vordergrund ist. */
  private lastTick: number | undefined;

  /** Das Nachfassen laeuft im Dienst, nicht in der Ansicht — ein Abruf zur Zeit, damit sich zwei nicht ueberholen. */
  private subscription: Subscription | undefined;

  public constructor() {
    super();
    document.addEventListener('visibilitychange', () => this.onVisibilityChange());
    this.lastTick = document.visibilityState === 'visible' ? Date.now() : undefined;
    window.setInterval(() => this.addForegroundTime(), NoticeService.TICK_MS);
  }

  /** Die anzuzeigende Mitteilung — `undefined`, wenn keine offen oder gerade weggeklickt ist. */
  public changed(): Observable<NoticeDto | undefined> {
    return this.notice.asObservable();
  }

  /**
   * Holt die nächste offene Mitteilung **für die Anzeige des Banners**.
   *
   * ⚠️ **Eine weggeklickte Mitteilung wird nicht sofort wieder gezeigt** — sonst wäre das Wegklicken wirkungslos und
   * jeder Seitenwechsel brächte sie zurück.
   */
  public load(): Observable<NoticeDto | undefined> {
    return this.noticeRepo.getOpen(this.getOrg())
      .pipe(map((w) => w.getDatas()?.[0]),
            tap((dto) => this.notice.next(this.isSuppressed(dto) ? undefined : dto)));
  }

  /**
   * Holt die offene Mitteilung **ohne Rücksicht auf den Aufschub** — für die Vertragsseite.
   *
   * 🔴 **Warum es diese zweite Methode gibt.** Wer das Banner wegklickt und danach über einen Link auf die
   * Vertragsseite geht, bekäme mit `load()` die Auskunft „kein Vertragsabschluss offen" — obwohl einer offen ist und
   * er gerade dorthin unterwegs war, um ihn abzuschließen. **Der Aufschub gilt für die Aufforderung, nicht für den
   * Weg zur Erfüllung.**
   */
  public loadForSigning(): Observable<NoticeDto | undefined> {
    return this.noticeRepo.getOpen(this.getOrg()).pipe(map((w) => w.getDatas()?.[0]));
  }

  public acknowledge(notice: NoticeDto, objection: boolean = false, reason?: string): Observable<void> {
    return this.noticeRepo.acknowledgeNotice(this.getOrg(), notice.id, objection, reason)
      .pipe(map(() => undefined), tap(() => this.answered()));
  }

  /**
   * „Administrator erinnern" — `core` verschickt die Mail.
   *
   * ⛔ **Die Mitteilung bleibt stehen:** Wer gebeten hat, hat nicht geantwortet.
   */
  public remind(notice: NoticeDto): Observable<NoticeRemindDto> {
    return this.noticeRepo.remindSignatories(this.getOrg(), notice.id).pipe(map((w) => w.getFirstData()));
  }

  public sign(notice: NoticeDto, authorizedConfirmed: boolean): Observable<void> {
    return this.noticeRepo.signNotice(this.getOrg(), notice.id, authorizedConfirmed)
      .pipe(map(() => undefined), tap(() => this.answered()));
  }

  /**
   * Wegklicken — die Mitteilung verschwindet und kommt nach 60 Minuten Vordergrundzeit wieder.
   *
   * ⛔ **Das ist keine Antwort auf die Mitteilung.** Es entsteht keine Zeile in `notice_ack`; wer eine `INFO` zur
   * Kenntnis nimmt, klickt „OK". Dies hier ist ein Aufschub, kein Nachweis.
   */
  public dismiss(notice: NoticeDto): void {
    sessionStorage.setItem(NoticeService.STORAGE_KEY, JSON.stringify({id: notice.id, ms: 0}));
    this.lastTick = document.visibilityState === 'visible' ? Date.now() : undefined;
    this.notice.next(undefined);
  }

  /** Nach einer echten Antwort ist der Aufschub gegenstandslos. */
  private answered(): void {
    sessionStorage.removeItem(NoticeService.STORAGE_KEY);
    this.notice.next(undefined);
  }

  private isSuppressed(dto: NoticeDto | undefined): boolean {
    if (notDefined(dto)) {
      return false;
    }
    const state: { id: string, ms: number } | undefined = this.readState();
    return isDefined(state) && state!.id === dto!.id && state!.ms < NoticeService.REMIND_AFTER_MS;
  }

  /**
   * ⚠️ **Beim Wechsel in den Hintergrund wird abgerechnet, nicht erst im nächsten Takt.** Wer den Tab genau zwischen
   * zwei Ticks wegschiebt, bekäme sonst bis zu 30 Sekunden geschenkt — bei jedem Wechsel.
   */
  private onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.lastTick = Date.now();
    } else {
      this.addForegroundTime();
      this.lastTick = undefined;
    }
  }

  private addForegroundTime(): void {
    if (document.visibilityState !== 'visible' || notDefined(this.lastTick)) {
      return;
    }
    const now: number = Date.now();
    const elapsed: number = now - (this.lastTick as number);
    this.lastTick = now;

    const state: { id: string, ms: number } | undefined = this.readState();
    if (notDefined(state)) {
      return;
    }
    const total: number = state!.ms + elapsed;
    sessionStorage.setItem(NoticeService.STORAGE_KEY, JSON.stringify({id: state!.id, ms: total}));

    /*
      🔴 Hier endet der Aufschub — und ohne diesen Zweig endete er nie.

      Der Zähler wuchs zwar, aber niemand las ihn wieder: `load()` läuft nur beim Wechsel der Organisation. Ein Klick
      auf „Später" hätte die Mitteilung damit für den Rest der Sitzung unterdrückt — genau das Gegenteil dessen, wofür
      die Vordergrundzeit gemessen wird. Punkt 13 des Zustimmungsplans verlangt das Nachfassen, und es findet hier
      statt oder gar nicht.
    */
    if (total >= NoticeService.REMIND_AFTER_MS) {
      sessionStorage.removeItem(NoticeService.STORAGE_KEY);
      this.subscription?.unsubscribe();
      this.subscription = this.load().subscribe();
    }
  }

  private readState(): { id: string, ms: number } | undefined {
    const raw: string | null = sessionStorage.getItem(NoticeService.STORAGE_KEY);
    if (notDefined(raw)) {
      return undefined;
    }
    try {
      return JSON.parse(raw as string);
    } catch {
      // Ein unlesbarer Eintrag darf die Anzeige nicht anhalten - dann eben ohne Aufschub.
      sessionStorage.removeItem(NoticeService.STORAGE_KEY);
      return undefined;
    }
  }
}
